// SSE-driven cache invalidation (RT-1/3) with a polling fallback + "reconnecting" state (RT-4).
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { PREFIX } from "../lib/queryKeys.js";

export type ConnState = "connecting" | "live" | "reconnecting";

const EVENT_TYPES = ["preprint.updated", "reviewer.updated", "coi.completed", "notification"] as const;

export function useLiveUpdates(): { state: ConnState } {
  const qc = useQueryClient();
  const [state, setState] = useState<ConnState>("connecting");
  const pollRef = useRef<number | null>(null);

  useEffect(() => {
    let es: EventSource | null = null;

    const invalidate = (prefixes: readonly string[]) => {
      for (const p of prefixes) void qc.invalidateQueries({ queryKey: [p] });
    };
    const onEvent = (type: (typeof EVENT_TYPES)[number]) => {
      if (type === "preprint.updated") invalidate(PREFIX.preprint);
      else if (type === "reviewer.updated") invalidate(PREFIX.reviewer);
      else if (type === "coi.completed") {
        invalidate(PREFIX.coi);
        invalidate(PREFIX.notification);
      } else if (type === "notification") invalidate(PREFIX.notification);
    };

    const startPolling = () => {
      if (pollRef.current != null) return;
      pollRef.current = window.setInterval(() => void qc.invalidateQueries(), 8000);
    };
    const stopPolling = () => {
      if (pollRef.current != null) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };

    const connect = () => {
      es = new EventSource("/api/stream", { withCredentials: true });
      es.addEventListener("ready", () => {
        setState("live");
        stopPolling();
      });
      es.onopen = () => {
        setState("live");
        stopPolling();
      };
      for (const t of EVENT_TYPES) {
        es.addEventListener(t, () => onEvent(t));
      }
      es.onerror = () => {
        // EventSource auto-reconnects; meanwhile fall back to polling (RT-4).
        setState("reconnecting");
        startPolling();
      };
    };
    connect();

    return () => {
      es?.close();
      stopPolling();
    };
  }, [qc]);

  return { state };
}
